import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Dialog, DialogContent, Stack, Typography, useMediaQuery } from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { AttendantAvatar } from '../AI/attendant/AttendantAvatar';
import { NetworkScoreService } from '../../services/networkScoreService';
import { IUnacknowledgedLevelUpResponse, getLevelColours } from '../../interfaces/INetworkScore';
import { RootState } from '../../redux/store';

interface LevelUpDialogProps {
    networkScoreService: NetworkScoreService;
}

/**
 * Safety-net poll interval. Of the thirteen scoring actions (network-score.md, section 3), ten
 * have an instant trigger: connection-accept and message-replies push live over SignalR (via
 * `connectionsVersion` below), and the rest of the self-initiated ones (messages, replies,
 * reconnects, pods, community posts, comments, joining a community) bump `scoreCheckVersion` from
 * their own success handlers. That leaves three with no discrete client-observable trigger — the
 * weekly-active-days bonus (server-derived), a post reaching 5 reactions (someone else's action),
 * and event check-in (organiser/kiosk-scanned) — which this poll exists to catch.
 */
const LEVEL_UP_POLL_INTERVAL_MS = 5 * 60 * 1000;

/** "a Connector" vs "an Ambassador" — only the level name's first letter matters here. */
const getIndefiniteArticle = (word: string): string => (/^[aeiou]/i.test(word) ? 'an' : 'a');

export const LevelUpDialog: React.FC<LevelUpDialogProps> = ({ networkScoreService }) => {
    const [levelUp, setLevelUp] = useState<IUnacknowledgedLevelUpResponse | null>(null);
    const [isAcknowledging, setIsAcknowledging] = useState(false);
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const connectionsVersion = useSelector((state: RootState) => state.chat.connectionsVersion);
    const scoreCheckVersion = useSelector((state: RootState) => state.score.scoreCheckVersion);
    // Guards the poll against overwriting a dialog that's already open and awaiting dismissal.
    const hasPendingLevelUpRef = useRef(false);

    useEffect(() => {
        let isMounted = true;

        const fetchLevelUp = async () => {
            if (hasPendingLevelUpRef.current) return;
            try {
                const unacknowledgedLevelUp = await networkScoreService.getUnacknowledgedLevelUp();
                if (isMounted && unacknowledgedLevelUp) {
                    hasPendingLevelUpRef.current = true;
                    setLevelUp(unacknowledgedLevelUp);
                }
            } catch {
                // Silent: a missed level-up celebration isn't worth surfacing an error for.
            }
        };

        fetchLevelUp();
        const pollId = setInterval(fetchLevelUp, LEVEL_UP_POLL_INTERVAL_MS);

        return () => {
            isMounted = false;
            clearInterval(pollId);
        };
    }, [networkScoreService, connectionsVersion, scoreCheckVersion]);


    if (!levelUp) return null;

    const handleDismiss = async () => {
        setIsAcknowledging(true);
        try {
            await networkScoreService.acknowledgeLevelUp(levelUp.toLevelName);
        } catch {
            // Silent: acknowledging is a courtesy to avoid re-showing the dialog, not critical.
        } finally {
            setIsAcknowledging(false);
            hasPendingLevelUpRef.current = false;
            setLevelUp(null);
        }
    };

    const badgeColour = getLevelColours(levelUp.badgeColourKey).solidBg;
    const habitClause = levelUp.hasHabitRequirement
        ? ' and your habits cleared the bar. That second part is the one most people miss.'
        : '.';

    return (
        <Dialog
            open
            disableEscapeKeyDown
            maxWidth="xs"
            fullWidth
            transitionDuration={prefersReducedMotion ? 0 : 350}
            aria-labelledby="level-up-dialog-title"
            slotProps={{ paper: { sx: { borderRadius: 4 } } }}
        >
            <DialogContent sx={{ textAlign: 'center', pt: 5, pb: 4 }}>
                <Stack spacing={3} alignItems="center">
                    <AttendantAvatar state="celebrating" size={96} />
                    <Box>
                        <Typography
                            variant="overline"
                            sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '0.12em' }}
                        >
                            Level {levelUp.toLevelNumber}
                        </Typography>
                        <Typography id="level-up-dialog-title" variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                            You&rsquo;re {getIndefiniteArticle(levelUp.toLevelName)} {levelUp.toLevelName}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            You passed {levelUp.pointsThreshold} points in the last {levelUp.rollingWindowDays} days{habitClause}
                        </Typography>
                    </Box>
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5, textAlign: 'left' }}
                        >
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: badgeColour, flexShrink: 0 }} />
                            <Typography variant="body2">
                                Your badge now reads {levelUp.toLevelName}. Connections see it from today.
                            </Typography>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5, textAlign: 'left' }}
                        >
                            <MenuBookOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />
                            <Typography variant="body2">
                                Lesson {levelUp.toLevelNumber} unlocked: {levelUp.lessonTitle}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleDismiss}
                        disabled={isAcknowledging}
                        sx={{
                            borderRadius: '999px',
                            py: 1.25,
                            background: 'var(--gradient-signature)',
                            color: 'common.white',
                            '&:hover': { background: 'var(--gradient-signature)', filter: 'brightness(1.05)' },
                        }}
                    >
                        {isAcknowledging ? <CircularProgress size={18} color="inherit" /> : 'Keep going'}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default LevelUpDialog;
